import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { getClerkToken } from "@/lib/clerkToken";

function normalizePath(path) {
  if (!path) return "";
  const p = String(path);
  if (p.startsWith("http://") || p.startsWith("https://")) return p;
  if (p.startsWith("/")) return api_endpoint + p;
  return api_endpoint + "/" + p;
}

async function getRequiredToken(getToken) {
  const token = await getClerkToken(getToken);
  if (!token) {
    const err = new Error("Not authenticated.");
    err.code = "NO_TOKEN";
    throw err;
  }
  return token;
}

async function getOptionalToken(getToken) {
  try {
    return await getClerkToken(getToken);
  } catch {
    return null;
  }
}

export async function apiRequest(getToken, opts) {
  const {
    method = "get",
    url,
    path,
    params,
    data,
    headers,
    auth = "required", // 'required' | 'optional' | 'none'
    signal,
    retryOnceOn401 = false,
  } = opts || {};

  const finalUrl = normalizePath(url ?? path);

  const token =
    auth === "required"
      ? await getRequiredToken(getToken)
      : auth === "optional"
        ? await getOptionalToken(getToken)
        : null;

  const config = {
    method,
    url: finalUrl,
    params,
    data,
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : null),
      ...(headers || null),
    },
  };

  try {
    const res = await axios.request(config);
    return res.data;
  } catch (err) {
    const status = err?.response?.status;
    const isAxiosError = axios.isAxiosError?.(err) ?? false;
    const code = err?.code;
    const message = err?.message;
    const name = err?.name;
    const canceled = code === "ERR_CANCELED" || name === "CanceledError";

    if (import.meta.env.DEV) {
      const upperMethod = String(method || "get").toUpperCase();
      const log = canceled ? console.debug : console.error;
      log(`[apiRequest] ${upperMethod} ${finalUrl} failed`, {
        status,
        code,
        name,
        message,
        isAxiosError,
        params,
        data,
        responseData: err?.response?.data,
      });

      if (!status) {
        const log2 = canceled ? console.debug : console.error;
        log2("[apiRequest] No HTTP response received", {
          canceled,
          hint: canceled
            ? "Request was canceled (often due to React Query abort signal/navigation)."
            : "Likely CORS/network/DNS/SSL issue. Check DevTools Network tab for (blocked:cors) or failed request.",
          url: finalUrl,
        });
      }
    }

    if (retryOnceOn401 && status === 401 && auth !== "none") {
      // Avoid the shared de-duper here; try to mint a fresh token if Clerk supports it.
      let freshToken = null;
      try {
        freshToken = await getToken?.({ skipCache: true });
      } catch {
        freshToken = await getToken?.();
      }

      if (freshToken) {
        const retryRes = await axios.request({
          ...config,
          headers: {
            ...(config.headers || {}),
            Authorization: `Bearer ${freshToken}`,
          },
        });
        return retryRes.data;
      }
    }
    throw err;
  }
}
